"use client";

import { memo, useCallback } from 'react';
import styles from './FilterChips.module.scss';
import Text from '@components/atoms/Text';
import Image from 'next/image';
import { useFilter } from '@context/FilterContext';
import { filterHelpers } from '@helpers/filterLogic';
import { FilterChipsProps } from '@interfaces/filter.types';
import Button from '../Button';
import assetLoader from '@utils/internalImageLoder';
import { usePathname, useRouter } from 'next/navigation';
import { createFilterChipsConfig } from '@helpers/filterConfigHelpers';
import { useSticky } from '@hooks/use-sticky';
import { getNativeHeaderHeight } from '@hooks/useAppBarUpdate';
import { trackEventWithCommonProps, EventType } from '@utils/eventTracker';
import { PAGE_TYPES } from '@constants/pageTypes';
import { useGlobalLoader } from '@hooks/useGlobalLoader';

const FilterChips = memo(({ 
  activeFilterType, 
  onFilterClick, 
  expressDeliveryChange, 
  expressDeliveryEnabled,
  onFilterButtonClick,
  hasFilterGroups,
  variant = 'default',
  cityName,
  analyticsPageType,
}: FilterChipsProps) => {

  const pathname = usePathname();
  const router = useRouter();
  const { showLoader } = useGlobalLoader();
  const { state, filterOptions, isInitialized } = useFilter();
  const isApp = variant === 'app';
  const nativeHeaderHeight = isApp ? getNativeHeaderHeight() : 0;
  const stickyOffset = isApp ? nativeHeaderHeight : 56;
  const { isSticky, elementHeight, elementRef, sentinelRef, placeholderRef } = useSticky({ offset: stickyOffset });
  
  const filterMetrics = filterHelpers.getFilterMetrics(state, filterOptions);
  
  const isExpressDeliveryCollection = filterHelpers.isExpressDeliveryCollection(pathname || "");
  
  const dynamicChips = createFilterChipsConfig(filterOptions);
  const hasAnyFilters = dynamicChips && dynamicChips.length > 0;
  const hasExpressDelivery = !isExpressDeliveryCollection;
  const shouldShowFilterCount = isInitialized && filterMetrics.totalActiveFilters > 0;

  const showStickyAppSearch = isApp && isSticky;

  const handleStickySearchClick = useCallback(async () => {
    showLoader('Loading...');
    await trackEventWithCommonProps('action', EventType.TRACK_EVENT, {
      pageType: analyticsPageType ?? PAGE_TYPES.BUY_HOMEPAGE,
      additionalProps: {
        action_type: 'tap_btn',
        action_name: 'tap_btn_search',
        city_name: cityName,
        section: 'sticky_filter_chips',
      },
    });
    router.push('/acko-app/search');
  }, [analyticsPageType, cityName, router, showLoader]);

  const chipsInner = (
    <>
        {(hasFilterGroups) && (
          <Button 
            className={`${styles.chip} ${variant === 'app' ? styles.appVariant : ''}`}
            onClick={onFilterButtonClick}
          >
            <Image 
              src={`${assetLoader()}/icons/Filter_funnel.svg`}
              alt="Filter_funnel"
              width={variant === 'app' ? 16 : 20}
              height={variant === 'app' ? 16 : 20}
              loading="lazy"
            />
            <Text>Filter</Text>
            {shouldShowFilterCount && (
              <Text className={styles.filterCount}>({filterMetrics.totalActiveFilters})</Text>
            )}
          </Button>
        )}

        {hasExpressDelivery && (
          <Button
            className={`${styles.chip} ${expressDeliveryEnabled ? styles.active : ''} ${variant === 'app' ? styles.appVariant : ''}`}
            onClick={() => expressDeliveryChange(!expressDeliveryEnabled)}
          >
            <Image 
              src={`${assetLoader()}/icons/thunder_dark.svg`}
              alt="Express Delivery"
              width={variant === 'app' ? 16 : 20}
              height={variant === 'app' ? 16 : 20}
              loading='lazy'
            />
            <Text>Express Delivery</Text>
            {expressDeliveryEnabled && (
              <Image 
                src={`${assetLoader()}/icons/Close_Dark.svg`}
                alt="Express Delivery Enabled"
                width={14}
                height={14}
                loading='lazy'
                className={styles.expressDeliveryCloseIcon}
              />
            )}
          </Button>
        )}

        {hasAnyFilters && dynamicChips.map((chip) => {
          const filterCount = filterMetrics.counts[chip.key as keyof typeof filterMetrics.counts] || 0;
          const isActive = activeFilterType === chip.key;
          
          return (
            <Button
              key={chip.key}
              className={`${styles.chip} ${isActive ? styles.active : ''} ${variant === 'app' ? styles.appVariant : ''}`}
              onClick={() => onFilterClick(chip.key)}
            >
              <Text>
                {filterHelpers.getFilterTitleWithCount(chip.title, filterCount)}
              </Text>
              <Image 
                src={chip.key === 'sort_by' ? `${assetLoader()}/icons/Arrow_down.svg` : `${assetLoader()}/icons/Chevron_down_dark.svg`}
                alt={chip.title}
                width={18}
                height={18}
                loading='lazy'
              />
            </Button>
          );
        })}
    </>
  );

  return (
    <>
      <div ref={sentinelRef} />
      {isSticky && (
        <div 
          ref={placeholderRef}
          style={{ height: elementHeight }}
          className={styles.placeholder}
        />
      )}
      <div 
        ref={elementRef}
        className={`${styles.container} ${isSticky ? styles.sticky : ''} ${isApp ? styles.appVariant : ''} ${showStickyAppSearch ? styles.stickyWithSearch : ''}`}
        style={isSticky ? { top: stickyOffset } : undefined}
      >
      {showStickyAppSearch ? (
        <div className={styles.stickyAppRow}>
          <div className={styles.scrollContainer}>
          <Button
            type="button"
            className={styles.stickySearchButton}
            onClick={handleStickySearchClick}
            aria-label="Search cars"
          >
            <Image
              src={`${assetLoader()}/icons/search_dark.webp`}
              alt=""
              width={16}
              height={16}
            />
            <Text className={styles.stickySearchButtonText}>Search</Text>
          </Button>
          <div className={styles.divider} />
            {chipsInner}
          </div>
        </div>
      ) : (
        <div className={styles.scrollContainer}>
          {chipsInner}
        </div>
      )}
    </div>
    </>
  );
});

FilterChips.displayName = "FilterChips";

export default FilterChips;
